import React, { useState, useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import BlogCard from '@/components/Blogs/BlogCard';
import PageLayout from '@/components/Layouts/PageLayout';
import Div from '@/shared/Div';
import dlv from 'dlv';
import { Categories } from '@/components/Blogs/';
import ssrErrorHandler from '@/errors/ssrErrorHandler';
import { NextPageContext } from 'next';
import { fetchBlogPage, getAllBlogs, getCategoryBlogs } from '@/actions/fetchBlogs';
import Container from '@/shared/Container';
import Map from '@/components/Map';
import Pagination from '@/shared/crisp/Pagination';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import useLayoutRenderer from '@/hooks/useLayoutRenderer';
import { CMS_COMPONENTS } from '@/constants/cms';
import TitleBanner from '@/shared/TitleBanner';

interface BlogListingProps {
  blogTypes: any[];
  handleCategoryChange: (newCategory: string) => void;
  activeTab: string;
  results: any[];
  renderBlogs: (blog: any) => JSX.Element;
  currentPage: number;
  pageCount: number;
  handlePageChange: (newPage: number) => void;
  pageSize: number;
  total: number;
}

const BlogListing: React.FC<BlogListingProps> = ({
  blogTypes,
  handleCategoryChange,
  activeTab,
  results,
  renderBlogs,
  currentPage,
  pageCount,
  handlePageChange,
  pageSize,
  total,
}) => {
  return (
    <Container className="pt-s5 lg:pb-s7">
      <Div className="relative w-full h-fit-content">
        <Categories
          tabItems={[{ label: 'All', id: 0, blogTypeId: 'All' }, ...blogTypes]}
          activeClassNames="text-cu-selected border-cu-selected md:font-extrabold flex justify-center items-center gap-s3"
          className=" flex overflow-x-auto whitespace-nowrap gap-[20px] md:gap-s10 border-b border-neutral-300"
          setActiveTab={handleCategoryChange}
          activeTab={activeTab}
        />
      </Div>

      <Map
        className="my-s7 grid grid-cols-1 gap-s4 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 md:gap-s6"
        list={results}
        render={renderBlogs}
      />
      <Pagination
        currentPage={currentPage}
        totalPages={pageCount}
        onPageChange={handlePageChange}
        blogsPerPage={pageSize}
        totalBlogs={total}
        paginationConfig={{
          siblingCount: 2,
          boundaryCount: 1,
        }}
      />
    </Container>
  );
};

const STATIC_MAP: { [key: string]: any } = {
  [CMS_COMPONENTS.SHARED.TITLE_BANNER]: TitleBanner,
  [CMS_COMPONENTS.BLOG.BLOG_LISTING]: BlogListing,
};

const PAGE_SIZE = 6;

const BlogPage = (props: any) => {
  const { blogs, categoryId, layout, seo } = props;
  const [allBlogs, setAllBlogs] = useState(blogs);
  const { query, push, pathname } = useRouter();

  const [activeTab, setActiveTab] = useState(categoryId || 'All');

  const fetch = async (pageSize: number, page: number, category?: string) => {
    if (category && category !== 'All') {
      return await getCategoryBlogs(category, pageSize, page);
    }
    return getAllBlogs(pageSize, page);
  };

  const { trigger, data, isLoading } = useTriggerRequest({ fetch });

  const initialPage = Number(query.p) || dlv(blogs, 'pagination.page', 1);
  const [currentPage, setCurrentPage] = useState(initialPage);

  const pageSizeFromUrl = Number(query.pageSize) || dlv(allBlogs, 'pagination.pageSize', 10);
  const [pageSize, setPageSize] = useState(pageSizeFromUrl);

  const { page, pageCount, total } = blogs?.pagination ?? {};
  const blogTypes = allBlogs ? (dlv(allBlogs, 'blogTypes', []) as any[]) : [];
  const results = dlv(allBlogs ?? {}, 'results', []);

  const handlePageChange = async (newPage: number) => {
    push(
      {
        pathname: pathname,
        query: { ...query, p: newPage },
      },
      undefined,
      { shallow: true },
    );
    setCurrentPage(newPage);
    trigger(PAGE_SIZE, newPage, activeTab);
  };

  const handleCategoryChange = async (newCategory: string) => {
    setActiveTab(newCategory);
    setCurrentPage(1);
  };

  useEffect(() => {
    trigger(PAGE_SIZE, currentPage, activeTab);
  }, [activeTab, currentPage]);

  useEffect(() => {
    if (!isLoading && data) {
      setAllBlogs(data);
    }
  }, [isLoading, data]);

  useEffect(() => {
    const pageFromUrl = Number(query.p);
    const newPageSize = Number(query.pageSize) || pageSize;

    if (pageFromUrl && pageFromUrl !== currentPage) {
      setCurrentPage(pageFromUrl);
    }

    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
    }
  }, [currentPage, pageSize, query.p, query.pageSize]);

  const renderBlogs = useCallback(
    (blog: any) => (
      <BlogCard
        publishedAt={blog.publishDate || blog.publishedAt}
        key={blog.id}
        blogId={blog.blogId}
        title={blog.title}
        media={blog.mobileImage}
        tags={blog.tags}
      />
    ),
    [],
  );

  const pageLayout = useLayoutRenderer(layout, STATIC_MAP, {
    [CMS_COMPONENTS.BLOG.BLOG_LISTING]: {
      blogTypes,
      handleCategoryChange,
      activeTab,
      results,
      renderBlogs,
      currentPage,
      pageCount,
      handlePageChange,
      pageSize,
      total,
    },
  });

  return <PageLayout seo={seo}>{pageLayout}</PageLayout>;
};

export const getServerSideProps = async (context: NextPageContext) => {
  try {
    const { query } = context;
    const p = Number(query.p) || 1;
    const categoryId = query.categoryId as string;

    let promises = [fetchBlogPage()];

    if (categoryId) {
      promises.push(getCategoryBlogs(categoryId, PAGE_SIZE, p));
    } else {
      promises.push(getAllBlogs(PAGE_SIZE, p));
    }

    const [page, blogs] = await Promise.all(promises);
    const { layout, seo } = page;

    return {
      props: {
        blogs,
        layout,
        seo,
        categoryId: categoryId || null,
      },
    };
  } catch (error) {
    return ssrErrorHandler(error, context);
  }
};

export default BlogPage;
